/**
 * Плагин визуализации для CSElement
 */

import { CSElementPlugin, LifecycleHooks } from '../types/plugin-interfaces';
import { ICSElement } from '../types/interfaces';
import { VisualizationManager } from '../visualization/VisualizationManager';
import { AsciiEngine } from '../visualization/engines/AsciiEngine';
import { SvgEngine } from '../visualization/engines/SvgEngine';
import { HtmlEngine } from '../visualization/engines/HtmlEngine';
import {
  VisualizationOptions,
  VisualizationResult,
  VisualizationFormat,
  VisualizationTheme
} from '../types/visualization-interfaces';

export class VisualizationPlugin implements CSElementPlugin {
  name = 'visualization';
  version = '1.0.0';
  description = 'Плагин для визуализации CSElement структур в различных форматах';
  dependencies = [];

  private manager: VisualizationManager;
  private isInstalled = false;
  private CSElementClass: any = null;

  constructor() {
    this.manager = new VisualizationManager();
  }

  /**
   * Установка плагина
   */
  install(CSElementClass?: any): void {
    if (this.isInstalled) {
      console.warn('🎨 Плагин визуализации уже установлен');
      return;
    }

    // Сохраняем ссылку на CSElementClass
    this.CSElementClass = CSElementClass;

    // Регистрируем движки визуализации
    this.registerEngines();

    // Добавляем методы к прототипу CSElement
    this.extendCSElement();

    // Настраиваем обработчики событий
    this.setupEventHandlers();

    // Регистрируем хуки жизненного цикла если передан CSElementClass
    if (CSElementClass) {
      const pluginManager = CSElementClass.plugins;
      
      if (this.hooks.afterCreate) {
        pluginManager.addHook('afterCreate', this.hooks.afterCreate);
      }
      
      if (this.hooks.afterAddElement) {
        pluginManager.addHook('afterAddElement', this.hooks.afterAddElement);
      }
      
      if (this.hooks.afterRemoveElement) {
        pluginManager.addHook('afterRemoveElement', this.hooks.afterRemoveElement);
      }
    } else {
      console.log('⚠️ CSElementClass не передан, хуки не регистрируются');
    }

    this.isInstalled = true;
    // console.log('🎨 Плагин визуализации установлен');
    // console.log(`📊 Поддерживаемые форматы: ${this.getSupportedFormats().join(', ')}`);
  }

  /**
   * Удаление плагина
   */
  uninstall(): void {
    if (!this.isInstalled) {
      return;
    }

    // Удаляем методы из прототипа
    this.removeCSElementExtensions();

    // Удаляем хуки жизненного цикла если есть сохраненная ссылка на CSElementClass
    if (this.CSElementClass) {
      const pluginManager = this.CSElementClass.plugins;
      
      if (this.hooks.afterCreate) {
        pluginManager.removeHook('afterCreate', this.hooks.afterCreate);
      }
      
      if (this.hooks.afterAddElement) {
        pluginManager.removeHook('afterAddElement', this.hooks.afterAddElement);
      }
      
      if (this.hooks.afterRemoveElement) {
        pluginManager.removeHook('afterRemoveElement', this.hooks.afterRemoveElement);
      }
    }

    // Очищаем менеджер
    this.manager.clear();

    // Очищаем ссылку на CSElementClass
    this.CSElementClass = null;

    this.isInstalled = false;
    // console.log('🎨 Плагин визуализации удален');
  }

  /**
   * Регистрация движков визуализации
   */
  private registerEngines(): void {
    this.manager.registerEngine(new AsciiEngine());
    this.manager.registerEngine(new SvgEngine());
    this.manager.registerEngine(new HtmlEngine());
  }

  /**
   * Расширение CSElement новыми методами
   */
  private extendCSElement(): void {
    // Используем сохраненную ссылку на CSElementClass
    const CSElementClass = this.CSElementClass;

    // Метод визуализации
    CSElementClass.prototype.visualize = async function(
      options: VisualizationOptions = {}
    ): Promise<VisualizationResult> {
      return await visualizationPlugin.manager.visualize(this, options);
    };

    // Метод быстрой ASCII визуализации
    CSElementClass.prototype.toAscii = async function(
      options: Partial<VisualizationOptions> = {}
    ): Promise<string> {
      const result = await this.visualize({ ...options, format: 'ascii' });
      return result.content;
    };

    // Метод экспорта в SVG
    CSElementClass.prototype.toSvg = async function(
      options: Partial<VisualizationOptions> = {}
    ): Promise<string> {
      const result = await this.visualize({ ...options, format: 'svg' });
      return result.content;
    };

    // Метод создания HTML визуализации
    CSElementClass.prototype.toHtml = async function(
      options: Partial<VisualizationOptions> = {}
    ): Promise<VisualizationResult> {
      return await this.visualize({ ...options, format: 'html' });
    };

    // Метод для вывода в консоль
    CSElementClass.prototype.print = async function(
      options: Partial<VisualizationOptions> = {}
    ): Promise<void> {
      const ascii = await this.toAscii(options);
      console.log(ascii);
    };

    // Статический метод для получения менеджера визуализации
    CSElementClass.getVisualizationManager = (): VisualizationManager => {
      return visualizationPlugin.manager;
    };

    // Статический метод для добавления пользовательских тем
    CSElementClass.addVisualizationTheme = (theme: VisualizationTheme): void => {
      visualizationPlugin.manager.addTheme(theme);
    };
  }

  /**
   * Удаление расширений CSElement
   */
  private removeCSElementExtensions(): void {
    // Используем сохраненную ссылку на CSElementClass
    const CSElementClass = this.CSElementClass;

    delete CSElementClass.prototype.visualize;
    delete CSElementClass.prototype.toAscii;
    delete CSElementClass.prototype.toSvg;
    delete CSElementClass.prototype.toHtml;
    delete CSElementClass.prototype.print;
    delete CSElementClass.getVisualizationManager;
    delete CSElementClass.addVisualizationTheme;
  }

  /**
   * Настройка обработчиков событий
   */
  private setupEventHandlers(): void {
    // Логирование событий визуализации
    this.manager.on('render:start', (_event) => {
      //console.log(`🎨 Начало рендеринга: ${event.options.format || 'ascii'}`);
    });

    this.manager.on('render:complete', (_event) => {
      //const { nodeCount, edgeCount, generationTime } = event.result!.metadata;
      //console.log(`✅ Рендеринг завершен: ${nodeCount} узлов, ${edgeCount} связей за ${generationTime}мс`);
    });

    this.manager.on('render:error', (event) => {
      console.error(`❌ Ошибка рендеринга:`, event.error);
    });
  }

  /**
   * Получить поддерживаемые форматы
   */
  getSupportedFormats(): VisualizationFormat[] {
    return this.manager.getStats().supportedFormats;
  }

  /**
   * Получить статистику плагина
   */
  getStats(): {
    enginesCount: number;
    themesCount: number;
    supportedFormats: VisualizationFormat[];
    isInstalled: boolean;
  } {
    return {
      ...this.manager.getStats(),
      isInstalled: this.isInstalled
    };
  }

  /**
   * Хуки жизненного цикла
   */
  hooks: LifecycleHooks = {
    afterCreate: (element: ICSElement) => {
      // Добавляем метаданные для визуализации
      if (!element.data.has('_visualization')) {
        // Используем прямой доступ к приватному полю _data
        (element as any)._data.set('_visualization', {
          created: Date.now(),
          rendered: false
        });
      }
    },

    afterAddElement: (element: ICSElement, _child: ICSElement) => {
      // Обновляем метаданные при добавлении дочерних элементов
      const vizData = element.data.get('_visualization') || {};
      vizData.lastModified = Date.now();
      vizData.rendered = false; // Требуется перерендеринг
      // Используем прямой доступ к приватному полю _data
      (element as any)._data.set('_visualization', vizData);
    },

    afterRemoveElement: (element: ICSElement, _child: ICSElement) => {
      // Обновляем метаданные при удалении дочерних элементов
      const vizData = element.data.get('_visualization') || {};
      vizData.lastModified = Date.now();
      vizData.rendered = false; // Требуется перерендеринг
      // Используем прямой доступ к приватному полю _data
      (element as any)._data.set('_visualization', vizData);
    }
  };
}

// Глобальный экземпляр плагина
export const visualizationPlugin = new VisualizationPlugin();

// Расширение интерфейса CSElement для TypeScript
declare module '../types/interfaces' {
  interface ICSElement {
    /**
     * Визуализировать элемент
     */
    visualize?(options?: VisualizationOptions): Promise<VisualizationResult>;

    /**
     * Получить ASCII представление
     */
    toAscii?(options?: Partial<VisualizationOptions>): Promise<string>;

    /**
     * Получить SVG представление
     */
    toSvg?(options?: Partial<VisualizationOptions>): Promise<string>;

    /**
     * Получить HTML представление
     */
    toHtml?(options?: Partial<VisualizationOptions>): Promise<VisualizationResult>;

    /**
     * Вывести ASCII представление в консоль
     */
    print?(options?: Partial<VisualizationOptions>): Promise<void>;
  }
}

// Расширение статических методов CSElement
declare module '../core/CSElement' {
  namespace CSElement {
    /**
     * Получить менеджер визуализации
     */
    function getVisualizationManager(): VisualizationManager;

    /**
     * Добавить пользовательскую тему
     */
    function addVisualizationTheme(theme: VisualizationTheme): void;
  }
} 